import { API_URL, getUserId } from './api';
import { getSyncSession } from './sync';

const PUSH_ENABLED_KEY = 'DirtyLingoPushEnabled';
const PUSH_DEVICE_ID_KEY = 'DirtyLingoPushDeviceId';
const PUBLIC_KEY = import.meta.env.VITE_PUSH_PUBLIC_KEY ?? '';

type PushSyncPayload = {
  subscription: PushSubscriptionJSON;
  userId: string;
  nextReviewAt: number | null;
};

function setEnabled(enabled: boolean) {
  localStorage.setItem(PUSH_ENABLED_KEY, enabled ? 'true' : 'false');
}

export function isPushEnabled() {
  return localStorage.getItem(PUSH_ENABLED_KEY) === 'true';
}

export function getPushDeviceId(): string {
  const existing = localStorage.getItem(PUSH_DEVICE_ID_KEY);
  if (existing) {
    return existing;
  }
  const id = crypto.randomUUID();
  localStorage.setItem(PUSH_DEVICE_ID_KEY, id);
  return id;
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

async function getSubscription() {
  const registration = await navigator.serviceWorker.register('/push-sw.js');
  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    return existing;
  }
  if (!PUBLIC_KEY) {
    throw new Error('Missing VITE_PUSH_PUBLIC_KEY');
  }
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY),
  });
}

async function sendSubscription(payload: PushSyncPayload) {
  if (!API_URL) {
    return;
  }
  await fetch(`${API_URL}/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

async function sendPushState(userId: string, nextReviewAt: number) {
  if (!API_URL) {
    return;
  }
  await fetch(`${API_URL}/push/state`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userId, nextReviewAt }),
  });
}

export async function enableReviewReminders(nextReviewAt: number | null) {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    setEnabled(false);
    return;
  }
  const subscription = await getSubscription();
  setEnabled(true);
  const session = getSyncSession();
  const userId = session?.userId ?? getUserId();
  if (nextReviewAt && nextReviewAt > Date.now()) {
    await sendSubscription({
      subscription: subscription.toJSON(),
      userId,
      nextReviewAt,
    });
  }
}

export async function disableReviewReminders() {
  setEnabled(false);
  const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription || !API_URL) {
    return;
  }
  await fetch(`${API_URL}/push/unsubscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      userId: getSyncSession()?.userId ?? getUserId(),
    }),
  });
  await subscription.unsubscribe();
}

export async function syncReviewReminders(nextReviewAt: number | null, options?: { allowStateUpdate?: boolean }) {
  const session = getSyncSession();
  const userId = session?.userId ?? getUserId();
  if (nextReviewAt && nextReviewAt > Date.now()) {
    if (isPushEnabled()) {
      const subscription = await getSubscription();
      await sendSubscription({
        subscription: subscription.toJSON(),
        userId,
        nextReviewAt,
      });
      return;
    }
    if (options?.allowStateUpdate) {
      await sendPushState(userId, nextReviewAt);
    }
  }
}

export async function clearPushNotifications() {
  if (!('serviceWorker' in navigator)) {
    return;
  }
  const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
  const notifications = await registration?.getNotifications();
  notifications?.forEach((notification) => notification.close());
}
